import type { ComponentType, ReactNode } from 'react';
import { cn } from '../../utils/cn';

type Tone = 'neutral' | 'accent' | 'danger' | 'warning' | 'info' | 'success';

interface EmptyStateProps {
  icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons/links rendered under the description. */
  action?: ReactNode;
  tone?: Tone;
  /** Smaller padding for use inside cards or narrow columns. */
  compact?: boolean;
  /** Render without the card surface (when already inside a card). */
  bare?: boolean;
  className?: string;
}

/** Centred empty / error / no-results state with an icon tile, title, copy and actions. */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  tone = 'neutral',
  compact = false,
  bare = false,
  className,
}: EmptyStateProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      !bare && 'glass-card',
      compact ? 'p-6' : 'p-10 md:p-14',
      className,
    )}
  >
    {Icon && (
      <span className={cn('icon-tile icon-tile-lg mb-3', `icon-tile-${tone}`)}>
        <Icon className="w-5 h-5" />
      </span>
    )}
    <h3 className="text-sm font-semibold text-white">{title}</h3>
    {description && <p className="mt-1 text-sm text-glass-400 max-w-sm">{description}</p>}
    {action && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{action}</div>}
  </div>
);
